import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, LayoutDashboard, Wand2, Star, Loader2, Tv, Youtube, FileSpreadsheet,
  Wallet, Target, Layers, PiggyBank, Calendar, Building2, Users as UsersIcon,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtCurrency, fmtNumber, fmtPercent, fmtFullCurrency } from "@/lib/format";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { setActiveCampaignId } = useActiveCampaign();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/dashboard/by-campaign/${id}`);
        setData(d);
      } catch {
        toast.error("Could not load campaign");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const onSelectScenario = async (scenarioId) => {
    try {
      await api.patch(`/scenarios/${scenarioId}/select`);
      toast.success("Scenario selected for Dashboard");
      const { data: d } = await api.get(`/dashboard/by-campaign/${id}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#0A3D91]" />
      </div>
    );
  }

  if (!data) return <div className="text-slate-500">Campaign not found.</div>;

  const camp = data.campaign;
  const kpis = data.kpis;
  const scenario = data.selected_scenario;
  const curveSet = data.curve_set;

  return (
    <div className="space-y-6 max-w-7xl">
      <button onClick={() => nav("/campaigns")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0A3D91]">
        <ArrowLeft className="h-4 w-4" /> Back to Campaigns
      </button>

      <div className="card-harmony overflow-hidden">
        <div className="harmony-accent-bar h-1.5" />
        <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Campaign</div>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">{camp.campaign_name}</h2>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {camp.client && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {camp.client}</span>}
              {camp.agency && <span>· {camp.agency}</span>}
              {camp.target_group && <span className="inline-flex items-center gap-1">· <UsersIcon className="h-3.5 w-3.5" /> {camp.target_group}</span>}
              {camp.date_start && <span className="inline-flex items-center gap-1">· <Calendar className="h-3.5 w-3.5" /> {camp.date_start} → {camp.date_end || "—"}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setActiveCampaignId(id); nav("/"); }} className="btn-secondary" data-testid="cd-open-dashboard">
              <LayoutDashboard className="h-4 w-4" /> Open Dashboard
            </button>
            <button onClick={() => { setActiveCampaignId(id); nav(`/optimizer?campaign=${id}`); }} className="btn-primary" data-testid="cd-open-optimizer">
              <Wand2 className="h-4 w-4" /> Open Optimizer
            </button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview" data-testid="cd-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="inputs" data-testid="cd-tab-inputs">Inputs</TabsTrigger>
          <TabsTrigger value="results" data-testid="cd-tab-results">Results</TabsTrigger>
          <TabsTrigger value="files" data-testid="cd-tab-files">Files</TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="cd-tab-scenarios">Scenarios ({data.campaign_scenarios?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total Budget" value={kpis.total_budget ? `${camp.currency || "IDR"} ${fmtCurrency(kpis.total_budget)}` : "—"} icon={Wallet} />
            <Stat label="TV Budget" value={kpis.tv_budget ? fmtCurrency(kpis.tv_budget) : "—"} sub={kpis.tv_allocation_pct ? `${kpis.tv_allocation_pct.toFixed(1)}%` : ""} icon={Tv} />
            <Stat label="OLV Budget" value={kpis.olv_budget ? fmtCurrency(kpis.olv_budget) : "—"} sub={kpis.olv_allocation_pct ? `${kpis.olv_allocation_pct.toFixed(1)}%` : ""} icon={Youtube} />
            <Stat label="Universe" value={fmtNumber(kpis.universe_size)} sub={camp.target_group} icon={UsersIcon} />
          </div>
          {data.warnings?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
              {data.warnings.map((w, i) => (
                <div key={`w-${i}`} className="text-xs text-amber-900">⚠ {w}</div>
              ))}
            </div>
          )}
          {camp.notes && (
            <div className="card-harmony p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-2">Notes</h3>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{camp.notes}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="inputs" className="mt-4">
          <div className="card-harmony p-5">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Campaign Inputs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="Campaign Name" value={camp.campaign_name} />
              <Row label="Status" value={camp.status} />
              <Row label="Client" value={camp.client} />
              <Row label="Agency" value={camp.agency} />
              <Row label="Currency" value={camp.currency || "IDR"} />
              <Row label="Effective Frequency" value={camp.effective_frequency ?? "—"} />
              <Row label="Target Group" value={camp.target_group} />
              <Row label="Universe Size" value={fmtNumber(camp.universe_size)} />
              <Row label="Start Date" value={camp.date_start} />
              <Row label="End Date" value={camp.date_end} />
              <Row label="Total Budget" value={fmtFullCurrency(camp.total_budget, camp.currency)} />
              <Row label="TV Budget" value={fmtFullCurrency(camp.tv_budget, camp.currency)} />
              <Row label="OLV Budget" value={fmtFullCurrency(camp.olv_budget, camp.currency)} />
              <Row label="Selected Curve Set" value={curveSet?.curve_name || "— not linked —"} />
              <Row label="DDF (TV × OLV)" value={kpis.ddf ? kpis.ddf.toFixed(3) : "—"} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="results" className="mt-4 space-y-4">
          {scenario ? (
            <>
              <div className="card-harmony p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display text-lg font-semibold text-slate-900">
                    Selected Scenario: <span className="text-[#0A3D91]">{scenario.scenario_name}</span>
                  </h3>
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <ResultTile label="TV GRP" value={fmtNumber(kpis.tv_grp)} icon={Tv} />
                  <ResultTile label="OLV Impressions" value={fmtNumber(kpis.olv_impressions)} icon={Youtube} />
                  <ResultTile label="TV Reach" value={fmtPercent(kpis.tv_reach)} icon={Target} />
                  <ResultTile label="OLV Reach" value={fmtPercent(kpis.olv_reach)} icon={Target} />
                  <ResultTile label="Combined Reach" value={fmtPercent(kpis.combined_reach)} icon={Layers} highlight />
                  <ResultTile label="Effective CPRP" value={`${camp.currency || "IDR"} ${fmtCurrency(kpis.effective_cprp)}`} icon={Wallet} />
                  <ResultTile label="TV-only GRP equiv." value={fmtNumber(kpis.tv_only_grp_equiv)} icon={Tv} />
                  <ResultTile label="Saving vs TV Only" value={`${camp.currency || "IDR"} ${fmtCurrency(kpis.saving_vs_tv_only)}`} icon={PiggyBank} highlight />
                </div>
              </div>
            </>
          ) : (
            <div className="card-harmony p-10 text-center text-sm text-slate-500">
              No scenario selected for this campaign.
              <button onClick={() => { setActiveCampaignId(id); nav(`/optimizer?campaign=${id}`); }} className="btn-primary mt-4">
                <Wand2 className="h-4 w-4" /> Run Optimizer
              </button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <div className="card-harmony p-5">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">
              Linked Curve Set Files
            </h3>
            {curveSet ? (
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-[#0A3D91]/5 border border-[#0A3D91]/10">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Curve Set</div>
                  <div className="font-medium text-slate-900">{curveSet.curve_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {curveSet.target_audience || "—"} · Universe {fmtNumber(curveSet.universe_size)}
                  </div>
                </div>
                {(curveSet.files || []).map((f) => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <FileSpreadsheet className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">{f.original_file_name}</div>
                      <div className="text-xs text-slate-500">
                        {f.media_type} · {f.input_metric} · {f.row_count} rows
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                      {f.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center">
                No curve set linked to this campaign.
                <button onClick={() => nav("/upload-curve")} className="btn-primary mt-4">
                  <FileSpreadsheet className="h-4 w-4" /> Upload Curve Files
                </button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="scenarios" className="mt-4">
          <div className="card-harmony overflow-hidden">
            {(!data.campaign_scenarios || data.campaign_scenarios.length === 0) ? (
              <div className="p-10 text-center text-sm text-slate-500">
                No scenarios yet.
                <button onClick={() => { setActiveCampaignId(id); nav(`/optimizer?campaign=${id}`); }} className="btn-primary mt-4">
                  <Wand2 className="h-4 w-4" /> Run First Scenario
                </button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4"></th>
                    <th className="text-left py-3 px-4">Scenario</th>
                    <th className="text-right py-3 px-4">TV / OLV Budget</th>
                    <th className="text-right py-3 px-4">TV Reach</th>
                    <th className="text-right py-3 px-4">OLV Reach</th>
                    <th className="text-right py-3 px-4">Combined</th>
                    <th className="text-right py-3 px-4">CPRP</th>
                    <th className="text-right py-3 px-4">Saving</th>
                    <th className="text-right py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.campaign_scenarios.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 hover:bg-slate-50 ${s.is_selected ? "bg-amber-50/40" : ""}`}>
                      <td className="py-3 px-4">
                        {s.is_selected && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-900">{s.scenario_name}</td>
                      <td className="py-3 px-4 text-right text-xs">
                        {fmtCurrency(s.tv_budget)} / {fmtCurrency(s.olv_budget)}
                      </td>
                      <td className="py-3 px-4 text-right">{fmtPercent(s.tv_reach)}</td>
                      <td className="py-3 px-4 text-right">{fmtPercent(s.olv_reach)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-[#0B8F80]">{fmtPercent(s.combined_reach)}</td>
                      <td className="py-3 px-4 text-right">{fmtCurrency(s.effective_cprp)}</td>
                      <td className="py-3 px-4 text-right text-emerald-600 font-semibold">{fmtCurrency(s.saving_vs_tv_only)}</td>
                      <td className="py-3 px-4 text-right">
                        {!s.is_selected && (
                          <button onClick={() => onSelectScenario(s.id)} className="text-xs text-[#0E73C9] hover:text-[#0A3D91] font-medium">
                            Use for Dashboard
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon }) {
  return (
    <div className="card-harmony p-4">
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="font-display text-xl font-bold text-slate-900 mt-2 leading-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value || "—"}</span>
    </div>
  );
}

function ResultTile({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`rounded-lg p-3 border border-slate-200 ${highlight ? "bg-gradient-to-br from-[#1CC7B3]/8 to-[#18B7D8]/5" : "bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="h-7 w-7 rounded-md flex items-center justify-center bg-[#0A3D91]/8 text-[#0A3D91]">
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="font-display text-lg font-bold text-slate-900 mt-1.5 leading-tight">{value}</div>
    </div>
  );
}
